import { useState, useEffect, useCallback } from 'react';
import { useMobileAuth } from '../../context/MobileAuthContext';
import MobileLayout from '../../components/mobile/MobileLayout';
import { ManageHeader, SearchBar } from '../../components/mobile/ManageKit';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import {
  Truck, Users, Shield, DollarSign, Settings2,
  Factory, Wrench, Calendar, Receipt, Layers, Workflow, MapPin, Folder, Ban,
  ChevronRight, ChevronLeft, Plus, Pencil, Trash2,
  Loader2,
} from 'lucide-react';

/* ─── Desktop-matching group/subtab definitions ─── */
const GROUPS = [
  {
    key: 'assets', label: 'Assets', icon: Truck, desc: 'Equipment types, makes & attachments',
    subtabs: [
      { key: 'equipment-types', label: 'Asset Types', icon: Truck, endpoint: '/api/equipment-types/all', postEndpoint: '/api/equipment-types' },
      { key: 'makes-models', label: 'Makes & Models', icon: Factory, endpoint: '/api/equipment-makes', postEndpoint: '/api/equipment-makes', isMakesModels: true },
      { key: 'attachment-types', label: 'Attachment Types', icon: Wrench, endpoint: '/api/attachment-types/all', postEndpoint: '/api/attachment-types' },
    ],
  },
  {
    key: 'workers', label: 'Workers', icon: Users, desc: 'Worker types, leave & allowances',
    subtabs: [
      { key: 'worker-types', label: 'Worker Types', icon: Users, endpoint: '/api/worker-types/all', postEndpoint: '/api/worker-types' },
      { key: 'leave-types', label: 'Leave Types', icon: Calendar, endpoint: '/api/leave-types', postEndpoint: '/api/leave-types' },
      { key: 'allowance-types', label: 'Allowances', icon: DollarSign, endpoint: '/api/allowance-types', postEndpoint: '/api/allowance-types' },
    ],
  },
  {
    key: 'compliance', label: 'Compliance', icon: Shield, desc: 'Licence & compliance categories',
    subtabs: [
      { key: 'compliance-types', label: 'Compliance Types', icon: Shield, endpoint: '/api/compliance-types/all', postEndpoint: '/api/compliance-types' },
    ],
  },
  {
    key: 'pricing', label: 'Pricing & Billing', icon: DollarSign, desc: 'Price lists, tolls & materials',
    subtabs: [
      { key: 'price-lists', label: 'Price Lists', icon: DollarSign, endpoint: '/api/price-lists', postEndpoint: '/api/price-lists' },
      { key: 'tolls', label: 'Tolls', icon: Receipt, endpoint: '/api/tolls', postEndpoint: '/api/tolls', hasPrice: true },
      { key: 'material-types', label: 'Material Types', icon: Layers, endpoint: '/api/material-types', postEndpoint: '/api/material-types' },
    ],
  },
  {
    key: 'operations', label: 'Operations', icon: Settings2, desc: 'Profiles, depots & folders',
    subtabs: [
      { key: 'setup-profiles', label: 'Setup Profiles', icon: Workflow, endpoint: '/api/setup-profiles', postEndpoint: '/api/setup-profiles' },
      { key: 'depots', label: 'Depots', icon: MapPin, endpoint: '/api/depots', postEndpoint: '/api/depots' },
      { key: 'schedule-folders', label: 'Schedule Folders', icon: Folder, endpoint: '/api/schedule-folders', postEndpoint: '/api/schedule-folders' },
      { key: 'cancellation-reasons', label: 'Cancellation Reasons', icon: Ban, endpoint: '/api/cancellation-reasons', postEndpoint: '/api/cancellation-reasons' },
    ],
  },
];

/* ─── Generic CRUD List ─── */
const CrudList = ({ api, subtab }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [editing, setEditing] = useState(null);
  const [editName, setEditName] = useState('');
  const [editPrice, setEditPrice] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(subtab.endpoint);
      const data = res.data;
      setItems(Array.isArray(data) ? data : data?.items || data?.data || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [api, subtab.endpoint]);

  useEffect(() => { load(); }, [load]);

  const startNew = () => { setEditing('new'); setEditName(''); setEditPrice(''); };
  const startEdit = (it) => { setEditing(it); setEditName(it.name || ''); setEditPrice(it.price != null ? String(it.price) : ''); };
  const cancel = () => { setEditing(null); setEditName(''); setEditPrice(''); };

  const save = async () => {
    if (!editName.trim()) return;
    setSaving(true);
    try {
      const payload = { name: editName.trim() };
      if (subtab.hasPrice) payload.price = parseFloat(editPrice) || 0;
      if (editing === 'new') {
        await api.post(subtab.postEndpoint, payload);
        toast.success('Created');
      } else {
        const ep = subtab.postEndpoint || subtab.endpoint;
        await api.put(`${ep}/${editing.id || editing._id}`, payload);
        toast.success('Updated');
      }
      cancel();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (it) => {
    try {
      const ep = subtab.postEndpoint || subtab.endpoint;
      await api.delete(`${ep}/${it.id || it._id}`);
      toast.success('Deleted');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    }
  };

  const filtered = items.filter((it) => (it.name || it.label || '').toLowerCase().includes(q.toLowerCase()));
  const currency = '$';

  return (
    <>
      <div className="px-4 pt-2 pb-2">
        <div className="flex items-center gap-2">
          <div className="flex-1"><SearchBar value={q} onChange={setQ} placeholder={`Search ${subtab.label.toLowerCase()}...`} /></div>
          <Button size="sm" className="btn-volt h-8 text-xs shrink-0" onClick={startNew} data-testid="data-add-btn">
            <Plus className="h-3.5 w-3.5 mr-1" /> Add
          </Button>
        </div>
      </div>

      {editing && (
        <div className="mx-4 p-3 bg-[var(--m-bg-card)] rounded-lg border border-[var(--m-border-light)] space-y-2 mb-2" data-testid="data-edit-form">
          <input autoFocus value={editName} onChange={(e) => setEditName(e.target.value)} placeholder="Name"
            className="w-full bg-[var(--m-bg)] border border-[var(--m-border-light)] rounded-lg px-3 py-2 text-sm text-[var(--m-text)] placeholder-[var(--m-text-muted)]"
            data-testid="data-edit-name" onKeyDown={(e) => e.key === 'Enter' && !subtab.hasPrice && save()} />
          {subtab.hasPrice && (
            <div className="flex items-center gap-2">
              <span className="text-[var(--m-text-secondary)] text-sm">{currency}</span>
              <input value={editPrice} onChange={(e) => setEditPrice(e.target.value)} placeholder="0.00" type="number" step="0.01" min="0"
                className="flex-1 bg-[var(--m-bg)] border border-[var(--m-border-light)] rounded-lg px-3 py-2 text-sm text-[var(--m-text)] placeholder-[var(--m-text-muted)]"
                data-testid="data-edit-price" onKeyDown={(e) => e.key === 'Enter' && save()} />
            </div>
          )}
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="flex-1 border-[var(--m-border-light)] text-[var(--m-text-body)]" onClick={cancel} data-testid="data-edit-cancel">Cancel</Button>
            <Button size="sm" className="flex-1 btn-volt" onClick={save} disabled={saving || !editName.trim()} data-testid="data-edit-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
            </Button>
          </div>
        </div>
      )}

      <div className="px-4 pb-4 space-y-1.5">
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-[var(--m-text-muted)]" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 text-[var(--m-text-muted)] text-sm" data-testid="data-empty-state">
            {q ? 'No matches found' : `No ${subtab.label.toLowerCase()} yet`}
          </div>
        ) : (
          filtered.map((it) => (
            <div key={it.id || it._id} className="flex items-center gap-3 px-3 py-2.5 rounded-lg bg-[var(--m-bg-card)]" data-testid={`data-item-${it.id || it._id}`}>
              <div className="flex-1 min-w-0">
                <span className="text-sm text-[var(--m-text)] truncate block">{it.name || it.label || 'Unnamed'}</span>
                {subtab.hasPrice && it.price != null && (
                  <span className="text-xs text-[var(--m-text-secondary)]">{currency}{Number(it.price).toFixed(2)}</span>
                )}
                {it.is_active === false && (
                  <span className="text-[10px] text-[var(--m-text-muted)] ml-1">(inactive)</span>
                )}
              </div>
              <button onClick={() => startEdit(it)} className="p-1.5 text-[var(--m-text-secondary)] hover:text-[var(--m-text)]"><Pencil className="h-3.5 w-3.5" /></button>
              <button onClick={() => remove(it)} className="p-1.5 text-[var(--m-text-secondary)] hover:text-red-400"><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          ))
        )}
      </div>
    </>
  );
};

/* ─── Makes & Models special list ─── */
const MakesModelsList = ({ api }) => {
  const [makes, setMakes] = useState([]);
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedMake, setExpandedMake] = useState(null);
  const [q, setQ] = useState('');
  const [editing, setEditing] = useState(null);
  const [editName, setEditName] = useState('');
  const [editType, setEditType] = useState('make'); // 'make' or 'model'
  const [editMakeId, setEditMakeId] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [makesRes, modelsRes] = await Promise.all([
        api.get('/api/equipment-makes'),
        api.get('/api/equipment-models'),
      ]);
      setMakes(Array.isArray(makesRes.data) ? makesRes.data : []);
      setModels(Array.isArray(modelsRes.data) ? modelsRes.data : []);
    } catch { /* no-op */ } finally { setLoading(false); }
  }, [api]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const cancel = () => { setEditing(null); setEditName(''); setEditType('make'); setEditMakeId(null); };

  const startNewMake = () => { setEditing('new'); setEditType('make'); setEditName(''); };
  const startNewModel = (makeId) => { setEditing('new'); setEditType('model'); setEditMakeId(makeId); setEditName(''); };
  const startEditMake = (m) => { setEditing(m); setEditType('make'); setEditName(m.name); };
  const startEditModel = (m) => { setEditing(m); setEditType('model'); setEditName(m.name); setEditMakeId(m.make_id); };

  const save = async () => {
    if (!editName.trim()) return;
    setSaving(true);
    try {
      if (editType === 'make') {
        if (editing === 'new') await api.post('/api/equipment-makes', { name: editName.trim() });
        else await api.put(`/api/equipment-makes/${editing.id || editing._id}`, { name: editName.trim() });
      } else {
        if (editing === 'new') await api.post('/api/equipment-models', { name: editName.trim(), make_id: editMakeId });
        else await api.put(`/api/equipment-models/${editing.id || editing._id}`, { name: editName.trim(), make_id: editMakeId });
      }
      toast.success(editing === 'new' ? 'Created' : 'Updated');
      cancel();
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); } finally { setSaving(false); }
  };

  const removeMake = async (m) => {
    try { await api.delete(`/api/equipment-makes/${m.id || m._id}`); toast.success('Deleted'); loadAll(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed to delete'); }
  };
  const removeModel = async (m) => {
    try { await api.delete(`/api/equipment-models/${m.id || m._id}`); toast.success('Deleted'); loadAll(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed to delete'); }
  };

  const filteredMakes = makes.filter((m) => (m.name || '').toLowerCase().includes(q.toLowerCase()));
  const modelsForMake = (makeId) => models.filter((m) => m.make_id === makeId);

  return (
    <>
      <div className="px-4 pt-2 pb-2">
        <div className="flex items-center gap-2">
          <div className="flex-1"><SearchBar value={q} onChange={setQ} placeholder="Search makes..." /></div>
          <Button size="sm" className="btn-volt h-8 text-xs shrink-0" onClick={startNewMake} data-testid="data-add-btn">
            <Plus className="h-3.5 w-3.5 mr-1" /> Make
          </Button>
        </div>
      </div>

      {editing && (
        <div className="mx-4 p-3 bg-[var(--m-bg-card)] rounded-lg border border-[var(--m-border-light)] space-y-2 mb-2" data-testid="data-edit-form">
          <div className="text-xs text-[var(--m-text-secondary)] mb-1">{editing === 'new' ? 'New' : 'Edit'} {editType === 'make' ? 'Make' : 'Model'}</div>
          <input autoFocus value={editName} onChange={(e) => setEditName(e.target.value)} placeholder="Name"
            className="w-full bg-[var(--m-bg)] border border-[var(--m-border-light)] rounded-lg px-3 py-2 text-sm text-[var(--m-text)] placeholder-[var(--m-text-muted)]"
            data-testid="data-edit-name" onKeyDown={(e) => e.key === 'Enter' && save()} />
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="flex-1 border-[var(--m-border-light)] text-[var(--m-text-body)]" onClick={cancel}>Cancel</Button>
            <Button size="sm" className="flex-1 btn-volt" onClick={save} disabled={saving || !editName.trim()}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
            </Button>
          </div>
        </div>
      )}

      <div className="px-4 pb-4 space-y-1.5">
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-[var(--m-text-muted)]" /></div>
        ) : filteredMakes.length === 0 ? (
          <div className="text-center py-12 text-[var(--m-text-muted)] text-sm" data-testid="data-empty-state">
            {q ? 'No matches found' : 'No makes yet'}
          </div>
        ) : (
          filteredMakes.map((make) => {
            const makeId = make.id || make._id;
            const isExpanded = expandedMake === makeId;
            const makeModels = modelsForMake(makeId);
            return (
              <div key={makeId} className="rounded-lg bg-[var(--m-bg-card)] overflow-hidden">
                <div className="flex items-center gap-3 px-3 py-2.5">
                  <button onClick={() => setExpandedMake(isExpanded ? null : makeId)}
                    className="flex-1 min-w-0 text-left flex items-center gap-2">
                    <ChevronRight className={`h-3.5 w-3.5 text-[var(--m-text-secondary)] transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                    <span className="text-sm text-[var(--m-text)] truncate">{make.name}</span>
                    <span className="text-[10px] text-[var(--m-text-muted)]">{makeModels.length} model{makeModels.length !== 1 ? 's' : ''}</span>
                  </button>
                  <button onClick={() => startEditMake(make)} className="p-1.5 text-[var(--m-text-secondary)] hover:text-[var(--m-text)]"><Pencil className="h-3.5 w-3.5" /></button>
                  <button onClick={() => removeMake(make)} className="p-1.5 text-[var(--m-text-secondary)] hover:text-red-400"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
                {isExpanded && (
                  <div className="px-3 pb-2.5 pl-8 space-y-1 border-t border-[var(--m-border)]/50">
                    {makeModels.map((model) => (
                      <div key={model.id || model._id} className="flex items-center gap-2 py-1.5">
                        <span className="text-xs text-[var(--m-text-body)] flex-1 truncate">{model.name}</span>
                        <button onClick={() => startEditModel(model)} className="p-1 text-[var(--m-text-muted)] hover:text-[var(--m-text)]"><Pencil className="h-3 w-3" /></button>
                        <button onClick={() => removeModel(model)} className="p-1 text-[var(--m-text-muted)] hover:text-red-400"><Trash2 className="h-3 w-3" /></button>
                      </div>
                    ))}
                    <button onClick={() => startNewModel(makeId)}
                      className="flex items-center gap-1.5 text-xs text-[#ffd108] hover:text-[#ffe55a] py-1" data-testid={`add-model-${makeId}`}>
                      <Plus className="h-3 w-3" /> Add Model
                    </button>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </>
  );
};

/* ─── Group detail view (sub-tab pills + content) ─── */
const GroupView = ({ api, group, onBack }) => {
  const [activeSubtab, setActiveSubtab] = useState(group.subtabs[0].key);
  const currentSub = group.subtabs.find((s) => s.key === activeSubtab);
  const GroupIcon = group.icon;

  return (
    <MobileLayout>
      <div className="bg-[var(--m-bg-card)] px-4 py-3 sticky top-0 z-10 safe-top">
        <div className="flex items-center gap-3">
          <button onClick={onBack} className="p-1 -ml-1 text-[var(--m-text-secondary)] hover:text-[var(--m-text)]" data-testid="data-back-btn">
            <ChevronLeft className="h-5 w-5" />
          </button>
          <GroupIcon className="h-5 w-5 text-[#ffd108]" />
          <h1 className="text-[var(--m-text)] font-bold text-base flex-1" data-testid="data-group-title">{group.label}</h1>
        </div>
      </div>

      {/* Sub-tab pills */}
      {group.subtabs.length > 1 && (
        <div className="px-4 pt-3 pb-1 flex flex-wrap gap-1.5 bg-[var(--m-bg-card)] sticky top-[52px] z-10 -mt-px border-b border-[var(--m-border)]/50" data-testid="data-subtabs">
          {group.subtabs.map((st) => {
            const Icon = st.icon;
            const isActive = activeSubtab === st.key;
            return (
              <button key={st.key} onClick={() => setActiveSubtab(st.key)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-md transition-colors mb-2
                  ${isActive ? 'bg-[#ffd108] text-black font-medium' : 'bg-[var(--m-bg)] text-[var(--m-text-secondary)] hover:text-[var(--m-text)]'}`}
                data-testid={`subtab-${st.key}`}>
                <Icon className="h-3.5 w-3.5" />
                {st.label}
              </button>
            );
          })}
        </div>
      )}

      {/* Content */}
      {currentSub?.isMakesModels ? (
        <MakesModelsList api={api} />
      ) : currentSub ? (
        <CrudList key={currentSub.key} api={api} subtab={currentSub} />
      ) : null}
    </MobileLayout>
  );
};

/* ─── Main hub ─── */
const MobileDataManagement = () => {
  const { api } = useMobileAuth();
  const [activeGroup, setActiveGroup] = useState(null);

  const group = GROUPS.find((g) => g.key === activeGroup);
  if (group) return <GroupView api={api} group={group} onBack={() => setActiveGroup(null)} />;

  return (
    <MobileLayout>
      <ManageHeader title="Data Management" subtitle="Manage system lookup data and configurations" backTo="/mobile/manage" />
      <div className="p-4 space-y-2" data-testid="data-management-hub">
        {GROUPS.map((g) => {
          const Icon = g.icon;
          return (
            <button key={g.key} onClick={() => setActiveGroup(g.key)}
              className="w-full flex items-center gap-4 px-4 py-3.5 rounded-lg border border-[var(--m-border)] bg-[var(--m-bg-card)] hover:bg-[var(--m-bg-elevated)] active:bg-[var(--m-bg-elevated)] transition-colors"
              data-testid={`data-group-${g.key}`}>
              <div className="w-9 h-9 rounded-lg bg-[var(--m-bg)] flex items-center justify-center shrink-0">
                <Icon className="h-5 w-5 text-[#ffd108]" />
              </div>
              <div className="flex-1 text-left">
                <div className="text-sm text-[var(--m-text)] font-medium">{g.label}</div>
                <div className="text-xs text-[var(--m-text-secondary)]">{g.desc}</div>
                <div className="text-[10px] text-[var(--m-text-muted)] mt-0.5">{g.subtabs.map((s) => s.label).join(' · ')}</div>
              </div>
              <ChevronRight className="h-4 w-4 text-[var(--m-text-muted)]" />
            </button>
          );
        })}
      </div>
    </MobileLayout>
  );
};

export default MobileDataManagement;
